import React, {createContext, type ReactNode, useContext, useMemo} from 'react';

import {getValue} from '../../util/runs';
import type {Key, Value} from '../../util/runTypes';
import type {RunSelectorRowType, RunToGetRow} from './util';

type RunSelectorRunsContextType<T extends RunToGetRow> = {
  baselineRun: RunSelectorRowType<T> | null | undefined;
  pinnedRuns: RunSelectorRowType<T>[] | null;
  runs: RunSelectorRowType<T>[] | null;
};

const RunSelectorRunsContext = createContext<
  RunSelectorRunsContextType<RunToGetRow>
>({
  baselineRun: null,
  pinnedRuns: [],
  runs: [],
});

type RunSelectorRunsProviderProps<T extends RunToGetRow> = {
  baselineRun: RunSelectorRowType<T> | null;
  children: ReactNode;
  pinnedRuns: RunSelectorRowType<T>[] | null;
  runs: RunSelectorRowType<T>[] | null;
};

export function RunSelectorRunsProvider<T extends RunToGetRow>({
  baselineRun,
  children,
  pinnedRuns,
  runs,
}: RunSelectorRunsProviderProps<T>) {
  const value = useMemo(
    () => ({
      baselineRun,
      pinnedRuns,
      runs,
    }),
    [baselineRun, pinnedRuns, runs]
  );

  // Justification: This context wraps the whole Run Selector and provides
  // a reference to all the rows currently being rendered (at the top level,
  // expanded groups of runs is a more-difficult kettle of fish, and should be
  // considered when it's needed. Each component in the tree might be interested
  // in what rows are in the table, and when the rows change the whole component
  // needs to be rerendered _anyway_ so this should be a low-cost context to add
  // to the Run Selector.
  return (
    <RunSelectorRunsContext.Provider value={value}>
      {children}
    </RunSelectorRunsContext.Provider>
  );
}

export function usePinnedRuns() {
  return useContext(RunSelectorRunsContext).pinnedRuns;
}

export function useRuns() {
  return useContext(RunSelectorRunsContext).runs;
}

/**
 * Returns the current baseline run, or null if no baseline is selected.
 */
export function useBaselineRun() {
  return useContext(RunSelectorRunsContext).baselineRun;
}

/**
 * Returns a summary metric value from the baseline run.
 * Only supports summary metrics; returns undefined for other key sections.
 * Throws if called outside a RunSelectorRunsProvider (via useBaselineRun).
 *
 * @param key - The metric key to look up. Must have section 'summary'.
 * @returns The metric value, or undefined if no baseline is selected or key is not a summary key.
 */
export function useBaselineRunSummaryMetric(key: Key): Value | undefined {
  const run = useBaselineRun();
  if (!run || key.section !== 'summary') {
    return undefined;
  }
  return getValue(run, key);
}
